import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { courses } from './CoursesGrid';
import {
  isSubscriptionTestModeEnabled,
  isSubscriptionTestModeAvailable,
  setSubscriptionTestMode,
} from '../utils/testingMode';
import { clearChapterCache } from '../utils/chapterApi';

const gatedUtilityPages = [
  { label: 'Quizzes', path: '/quizzes', note: 'Random quiz and premium topic access' },
  { label: 'Flashcards', path: '/flashcards', note: 'Random cards and premium topic access' },
  { label: 'Roadmap', path: '/roadmap', note: 'Premium roadmap nodes' },
  { label: 'Profile', path: '/profile', note: 'Premium account surfaces' },
  { label: 'Subscription', path: '/subscription', note: 'Plan and billing state' },
];

export default function SubscriptionTestingMode() {
  const [enabled, setEnabled] = useState(() => isSubscriptionTestModeEnabled());
  const available = isSubscriptionTestModeAvailable();
  const premiumCourses = useMemo(() => courses.filter((course) => course.premium), []);

  useEffect(() => {
    setSubscriptionTestMode(enabled);
    clearChapterCache();
  }, [enabled]);

  const handleToggle = (event) => {
    setEnabled(event.target.checked);
  };

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 5, md: 7 }, minHeight: 'calc(100vh - 120px)' }}>
      <Box sx={{ maxWidth: 820, mb: 4 }}>
        <Chip
          label="Local testing"
          sx={{ mb: 2, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
        />
        <Typography variant="h2" sx={{ color: 'text.primary', fontWeight: 850, lineHeight: 0.95, mb: 2 }}>
          Subscription-gated pages.
        </Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: '1.08rem', lineHeight: 1.7 }}>
          Use this page to inspect premium pages without changing real billing state. The bypass is stored only in this browser.
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 850 }}>
                Testing subscription bypass
              </Typography>
              <Typography sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                {!available
                  ? 'Unavailable on this host. Use localhost or set REACT_APP_ENABLE_SUBSCRIPTION_TEST_MODE=true.'
                  : enabled
                    ? 'Enabled. Premium UI and local chapter data are unlocked in this browser.'
                    : 'Disabled. The app uses normal auth and subscription checks.'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography sx={{ color: 'text.secondary', fontWeight: 750 }}>{enabled ? 'On' : 'Off'}</Typography>
              <Switch checked={enabled} onChange={handleToggle} disabled={!available} />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 850, mb: 2 }}>
        Premium courses
      </Typography>
      <Grid container spacing={2.2} sx={{ mb: 5 }}>
        {premiumCourses.map((course) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={course.id}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: 38,
                      height: 38,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      bgcolor: 'action.hover',
                    }}
                  >
                    {course.icon}
                  </Box>
                  <Box>
                    <Typography sx={{ color: 'text.primary', fontWeight: 850 }}>{course.name}</Typography>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.82rem' }}>{course.sectionsCount} sections</Typography>
                  </Box>
                </Stack>
                <Button component={RouterLink} to={`/courses/${course.id}`} variant="outlined" fullWidth>
                  Open course
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h5" sx={{ color: 'text.primary', fontWeight: 850, mb: 2 }}>
        Other gated surfaces
      </Typography>
      <Grid container spacing={2.2}>
        {gatedUtilityPages.map((page) => (
          <Grid item xs={12} md={4} key={page.path}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Typography sx={{ color: 'text.primary', fontWeight: 850, mb: 0.5 }}>{page.label}</Typography>
                <Typography sx={{ color: 'text.secondary', lineHeight: 1.6, mb: 2 }}>{page.note}</Typography>
                <Button component={RouterLink} to={page.path} variant="outlined">
                  Open page
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
}
